import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Award, DollarSign, Settings, Plus } from 'lucide-react';

import { useContributorAdmin } from './useContributorAdmin';
import ContributorsList from './ContributorsList';
import PointsSummary from './PointsSummary';
import PayoutsManager from './PayoutsManager';
import ContributorSettings from './ContributorSettings';
import { AssignContributorModal, AdjustPointsModal, ManualRevenueModal } from './ContributorModals';

const AdminContributorsTab = () => {
  const [activeTab, setActiveTab] = useState('contributors');
  const [expandedContributor, setExpandedContributor] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showManualRevenueModal, setShowManualRevenueModal] = useState(false);
  const [selectedContributor, setSelectedContributor] = useState(null);
  
  const {
    contributors,
    allUsers,
    loading,
    selectedMonth,
    setSelectedMonth,
    monthlyRevenue,
    payouts,
    settings,
    setSettings,
    savingSettings,
    fetchMonthlyRevenue,
    fetchPayouts,
    handleSaveSettings,
    handleAssignContributor,
    handleRemoveContributor,
    handleAdjustPoints,
    handleFinalizeRevenue,
    handleManualRevenue,
    handleGeneratePayouts,
    handleUpdatePayout,
  } = useContributorAdmin();
  
  useEffect(() => {
    if (activeTab === 'payouts') {
      fetchMonthlyRevenue();
      fetchPayouts();
    }
  }, [selectedMonth, activeTab, fetchMonthlyRevenue, fetchPayouts]);
  
  const openAdjustModal = (contributor) => {
    setSelectedContributor(contributor);
    setShowAdjustModal(true);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Contributor Compensation</h2>
          <p className="text-gray-400">Manage practitioners and their compensation</p>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="bg-slate-800 inline-flex w-auto min-w-full sm:w-full">
            <TabsTrigger value="contributors" className="data-[state=active]:bg-cyan-600 flex-shrink-0">
              <Users className="w-4 h-4 mr-2" />
              Contributors
            </TabsTrigger>
            <TabsTrigger value="points" className="data-[state=active]:bg-cyan-600 flex-shrink-0">
              <Award className="w-4 h-4 mr-2" />
              Points
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-cyan-600 flex-shrink-0">
              <DollarSign className="w-4 h-4 mr-2" />
              Payouts
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-cyan-600 flex-shrink-0">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Contributors Tab */}
        <TabsContent value="contributors" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-400">
              {contributors.filter(c => c.is_active).length} active contributors (Eligibility: {settings.eligibility_threshold} pts)
            </p>
            <Button 
              className="bg-cyan-600 hover:bg-cyan-700"
              onClick={() => setShowAssignModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Assign Contributor
            </Button>
          </div>
          
          <ContributorsList
            contributors={contributors}
            settings={settings}
            expandedContributor={expandedContributor}
            setExpandedContributor={setExpandedContributor}
            onAdjustPoints={openAdjustModal}
            onRemove={handleRemoveContributor}
          />
        </TabsContent>
        
        {/* Points Summary Tab */}
        <TabsContent value="points" className="space-y-4 mt-4">
          <PointsSummary
            contributors={contributors}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            onAdjustPoints={openAdjustModal}
          />
        </TabsContent>
        
        {/* Payouts Tab */}
        <TabsContent value="payouts" className="space-y-4 mt-4">
          <PayoutsManager
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            monthlyRevenue={monthlyRevenue}
            payouts={payouts}
            settings={settings}
            onFinalizeRevenue={handleFinalizeRevenue}
            onGeneratePayouts={handleGeneratePayouts}
            onUpdatePayout={handleUpdatePayout}
            onOpenManualRevenue={() => setShowManualRevenueModal(true)}
          />
        </TabsContent>
        
        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4 mt-4">
          <ContributorSettings
            settings={settings}
            setSettings={setSettings}
            savingSettings={savingSettings}
            onSave={handleSaveSettings}
          />
        </TabsContent>
      </Tabs>
      
      {/* Modals */}
      <AssignContributorModal
        open={showAssignModal}
        onOpenChange={setShowAssignModal}
        allUsers={allUsers}
        contributors={contributors}
        onAssign={handleAssignContributor}
      />
      
      <AdjustPointsModal
        open={showAdjustModal}
        onOpenChange={setShowAdjustModal}
        selectedContributor={selectedContributor}
        onAdjust={handleAdjustPoints}
      />
      
      <ManualRevenueModal
        open={showManualRevenueModal}
        onOpenChange={setShowManualRevenueModal}
        settings={settings}
        onSave={handleManualRevenue}
      />
    </div>
  );
};

export default AdminContributorsTab;
